import * as THREE from 'three/webgpu';
import {
    AbstractInteractionVisualState,
    GeometryInteractionState,
    MaterialApplySource,
    type FeatureVisualScope,
    type InteractionVisualStateContext,
    type InteractionVisualStateRuntime,
} from './AbstractInteractionVisualState';
import type { Viewer } from '../../viewer/Viewer';
import { type FeatureResultMesh, isFeatureResultMesh } from '../../mesh/feature/FeatureResultMesh';
import { ImportedModelFeatureTreeVisualApplicator } from '../../imported-model/interaction/ImportedModelFeatureTreeVisualApplicator';
import { ImportedModelBodyTreeSelectionFaceHighlighter } from '../../imported-model/interaction/ImportedModelBodyTreeSelectionFaceHighlighter';
import { ImportedModelSelectionObjectResolver } from '../../imported-model/selection/ImportedModelSelectionObjectResolver';
import {
    ImportedModelInstancedTopologyFaceVisualProjector,
} from '../../imported-model/instancing/selection';

//------ 特征基础视觉状态 开始------
/**
 * 特征基础视觉状态。
 * 说明：
 * 1. 该状态用于特征实体、普通基准对象与基础草图对象的 hover / selected 视觉反馈；
 * 2. 该类名称保留历史 `Default`，但语义上表示“特征基础视觉状态”，不是装配体空闲态的默认状态；
 * 3. 特征基础视觉状态不提供“轮廓选中/移入 -> 关联边高亮”能力；
 * 4. 后续若特征基础状态与草图状态出现视觉差异，可在本类扩展而不影响草图状态。
 */
export class DefaultTransitionState extends AbstractInteractionVisualState {
    /** imported-model 设计树视觉应用器。 */
    private readonly importedModelFeatureTreeApplicator: ImportedModelFeatureTreeVisualApplicator;
    /** imported-model 设计树 Body 整模型覆盖高亮器。 */
    private readonly importedBodyTreeModelHighlighter: ImportedModelBodyTreeSelectionFaceHighlighter;
    /** imported-model 选择对象解析器。 */
    private readonly importedModelSelectionObjectResolver: ImportedModelSelectionObjectResolver;
    /** 实例化 imported-model 拓扑面的单槽视觉投影器。 */
    private readonly importedModelInstancedTopologyFaceVisualProjector: ImportedModelInstancedTopologyFaceVisualProjector;

    /**
     * 构造函数。
     * @param runtime 状态运行时。
     * @param viewer Viewer 实例。
     */
    public constructor(runtime: InteractionVisualStateRuntime, viewer: Viewer) {
        super(runtime, viewer);
        this.importedModelSelectionObjectResolver = new ImportedModelSelectionObjectResolver({
            scene: this.viewer.scene,
            getSceneObjectById: (objectId: string) => this.viewer.findSceneObjectById(objectId),
        });
        this.importedModelInstancedTopologyFaceVisualProjector = new ImportedModelInstancedTopologyFaceVisualProjector(
            sourceMesh => this.viewer.resolveImportedModelInstancedMeshUnit(sourceMesh),
        );
        this.importedModelFeatureTreeApplicator = new ImportedModelFeatureTreeVisualApplicator({
            viewer: this.viewer,
            runtime: this.runtime,
        });
        this.importedBodyTreeModelHighlighter = new ImportedModelBodyTreeSelectionFaceHighlighter({
            unitSource: this.viewer.getImportedModelInstancedUnitSource?.(),
            idleRenderScheduler: this.viewer,
            membershipSource: this.viewer.meshManager?.getSceneObjectMembershipSource?.(),
            getSourceRevision: () => this.viewer.getImportedModelRenderSourceRevision?.() ?? 0,
            listSourceHierarchyChildren: this.viewer.listImportedModelSourceHierarchyChildren
                ? object => this.viewer.listImportedModelSourceHierarchyChildren(object) : undefined,
            isImportedModelObject: (object) => this.importedModelSelectionObjectResolver.isImportedModelObject(object),
            resolveInstancedMeshUnit: (sourceMesh) => (
                this.viewer.resolveImportedModelInstancedMeshUnit(sourceMesh)
            ),
            findSceneObjectById: (objectId) => this.viewer.findSceneObjectById(objectId),
            listInstancedSourceMeshes: (rootObject) => (
                this.viewer.listImportedModelInstancedSourceMeshes?.(rootObject) ?? []
            ),
            getSelectedBodyMaterial: () => (
                this.importedModelFeatureTreeApplicator.getImportedModelTreeSelectedBodyMaterial()
            ),
        });
    }

    //------ 正式基准对象状态补齐 开始------
    /**
     * 刷新视觉状态。
     *
     * 说明：
     * 1. 先复用抽象基类逻辑处理草图对象与特征对象；
     * 2. 再补正式基准面/轴/点的 hover 与 selected；
     * 3. 直接打开导入零件时，额外应用 imported-model 设计树 hover / select 视觉语义。
     *
     * @param context 当前刷新上下文。
     */
    public override refreshVisualState(context: InteractionVisualStateContext): void {
        const baseContext = this.importedModelFeatureTreeApplicator.createFilteredBaseContext(context);
        super.refreshVisualState(baseContext);
        this.importedModelInstancedTopologyFaceVisualProjector.apply(baseContext);
        //------ 设计树程序化特征移入高亮 开始------
        this.applySelectedFeatureInteractionStates(baseContext);
        //------ 设计树程序化特征移入高亮 结束------
        //------ 设计树程序化特征移入高亮 开始------
        this.applyProgrammaticFeatureInteractionStates(baseContext);
        //------ 设计树程序化特征移入高亮 结束------
        this.applyDatumReferenceInteractionStates(baseContext);
        this.importedBodyTreeModelHighlighter.apply(context);
        this.importedModelFeatureTreeApplicator.applyFeatureTreeVisualState(context);
    }

    /**
     * 重置视觉状态。
     */
    public override resetVisualState(): void {
        this.importedModelInstancedTopologyFaceVisualProjector.reset();
        super.resetVisualState();
        this.importedBodyTreeModelHighlighter.reset();
        this.importedModelFeatureTreeApplicator.resetVisualState();
    }

    /** 结束状态生命周期时释放热态选择缓存，不能仅隐藏派生批次。 */
    public override dispose(): void {
        super.dispose();
        this.importedBodyTreeModelHighlighter.dispose();
    }

    /**
     * 判断当前对象是否应被默认态材质接管。
     *
     * 设计说明：
     * imported-model 子树中的拓扑代理不应被基础状态机重写材质，
     * 否则设计树交互会污染代理原始材质并引发卡顿。
     *
     * @param object 待判断对象。
     * @param context 当前刷新上下文。
     * @returns 当前对象允许进入默认态材质接管时返回 `true`。
     */
    protected override shouldApplyDefaultMaterialState(
        object: THREE.Object3D,
        context: InteractionVisualStateContext,
    ): boolean {
        if (!super.shouldApplyDefaultMaterialState(object, context)) {
            return false;
        }

        return !this.importedModelFeatureTreeApplicator.isImportedModelTopologyProxyObject(object);
    }

    //------ 设计树程序化特征移入高亮 开始------
    /**
     * 应用程序化特征移入联动高亮。
     *
     * 设计说明：
     * 1. 设计树移入特征节点时，移入态只需要显式反馈该结果体所属的全部特征线；
     * 2. 该逻辑只消费 InteractionStateManager 注入的程序化结果体，不改变普通视口 hover 语义；
     * 3. 通过复用 `setObjectState`，可以继续遵守统一的材质快照、状态转移与恢复机制。
     *
     * @param context 当前刷新上下文。
     */
    private applyProgrammaticFeatureInteractionStates(context: InteractionVisualStateContext): void {
        const featureResultMesh = context.programmaticHoveredFeatureResultMesh;
        if (featureResultMesh) {
            this.highlightFeatureLinesByFeature(featureResultMesh, context);
        }
        const featureVisualScope = context.programmaticHoveredFeatureVisualScope;
        if (featureVisualScope) {
            this.highlightFeatureLinesByScope(featureVisualScope, context);
        }
    }

    /**
     * 根据当前选中特征结果体联动高亮其全部特征面。
     *
     * 设计说明：
     * 1. 设计树点击特征节点时，主选对象仍是 `FeatureResultMesh`；
     * 2. 面高亮语义应只在选中态出现，因此这里单独把“结果体选中 -> 所有面 Selected”收口；
     * 3. 这样可以和“hover 只高亮线”形成明确分工，避免面在移入态过度抢眼。
     *
     * @param context 当前刷新上下文。
     */
    private applySelectedFeatureInteractionStates(context: InteractionVisualStateContext): void {
        const featureVisualScope = context.selectedFeatureVisualScope;
        const scopedResultMeshIds = new Set(
            featureVisualScope?.resultMeshes.map((featureResultMesh) => featureResultMesh.uuid) ?? [],
        );
        context.selectedObjects.forEach((selectedObject) => {
            if (!isFeatureResultMesh(selectedObject)) {
                return;
            }
            if (scopedResultMeshIds.has(selectedObject.uuid)) {
                return;
            }
            this.highlightFeatureFacesAsSelected(selectedObject, context);
        });
        if (featureVisualScope) {
            this.highlightFeatureScopeAsSelected(featureVisualScope, context);
        }
    }

    /**
     * 根据特征结果体高亮其全部特征线。
     *
     * @param featureResultMesh 目标特征结果体。
     * @param context 当前刷新上下文。
     */
    private highlightFeatureLinesByFeature(
        featureResultMesh: FeatureResultMesh,
        context: InteractionVisualStateContext,
    ): void {
        const selectedObjectIdSet = new Set(context.selectedObjects.map((object) => object.uuid));
        featureResultMesh.getAllFeatureLines().forEach((featureLine) => {
            if (selectedObjectIdSet.has(featureLine.uuid)) {
                return;
            }
            this.setObjectState(featureLine, GeometryInteractionState.Hover, MaterialApplySource.Direct, context);
        });
    }

    /**
     * 根据设计树特征视觉范围高亮该特征贡献的边。
     *
     * @param scope 设计树特征视觉范围。
     * @param context 当前刷新上下文。
     */
    private highlightFeatureLinesByScope(
        scope: FeatureVisualScope,
        context: InteractionVisualStateContext,
    ): void {
        const selectedObjectIdSet = new Set(context.selectedObjects.map((object) => object.uuid));
        scope.resultMeshes.forEach((featureResultMesh) => {
            this.collectFeatureContributionLines(featureResultMesh, scope.featureId).forEach((featureLine) => {
                if (selectedObjectIdSet.has(featureLine.uuid)) {
                    return;
                }
                this.setObjectState(featureLine, GeometryInteractionState.Hover, MaterialApplySource.Direct, context);
            });
        });
    }

    /**
     * 收集指定特征贡献的全部可视边代理。
     *
     * 设计说明：
     * 1. 相交合并后的历史特征可能只剩直接归属于该特征的 edge，而没有独立 face；
     * 2. `hasFeatureContribution()` 已允许 edge 贡献成立，视觉反馈也必须消费同一语义；
     * 3. 这里把 face boundary 与 feature-owned edge 合并去重，避免 hover / selected 两条路径各自漏处理。
     *
     * @param featureResultMesh 特征结果体。
     * @param featureId 特征 ID。
     * @returns 该特征贡献的可视边代理。
     */
    private collectFeatureContributionLines(
        featureResultMesh: FeatureResultMesh,
        featureId: string,
    ): THREE.Object3D[] {
        const featureLineById = new Map<string, THREE.Object3D>();
        [
            ...featureResultMesh.getFeatureBoundaryLinesByOwningFeatureId(featureId),
            ...featureResultMesh.getFeatureLinesByOwningFeatureId(featureId),
        ].forEach((featureLine) => {
            featureLineById.set(featureLine.uuid, featureLine);
        });

        return Array.from(featureLineById.values());
    }

    /**
     * 根据特征结果体将其全部特征面设置为选中态。
     *
     * @param featureResultMesh 目标特征结果体。
     * @param context 当前刷新上下文。
     */
    private highlightFeatureFacesAsSelected(
        featureResultMesh: FeatureResultMesh,
        context: InteractionVisualStateContext,
    ): void {
        featureResultMesh.getAllFeatureFaces().forEach((featureFace) => {
            this.setObjectState(featureFace, GeometryInteractionState.Selected, MaterialApplySource.Direct, context);
        });
    }

    /**
     * 根据设计树特征视觉范围高亮该特征贡献的面与边。
     *
     * @param scope 设计树特征视觉范围。
     * @param context 当前刷新上下文。
     */
    private highlightFeatureScopeAsSelected(
        scope: FeatureVisualScope,
        context: InteractionVisualStateContext,
    ): void {
        scope.resultMeshes.forEach((featureResultMesh) => {
            featureResultMesh.getFeatureFacesByOwningFeatureId(scope.featureId).forEach((featureFace) => {
                this.setObjectState(featureFace, GeometryInteractionState.Selected, MaterialApplySource.Direct, context);
            });
            this.collectFeatureContributionLines(featureResultMesh, scope.featureId).forEach((featureLine) => {
                this.setObjectState(featureLine, GeometryInteractionState.Selected, MaterialApplySource.Direct, context);
            });
        });
    }
    //------ 设计树程序化特征移入高亮 结束------

    //------ 正式基准对象状态补齐 结束------

    /**
     * 特征基础视觉状态禁用轮廓联动边移入高亮。
     * 说明：选中轮廓联动边高亮也不在特征基础视觉状态实现。
     * @param context 当前刷新上下文。
     * @param hoveredObject 当前移入对象。
     */
    protected override highlightContourLinkedEdgesOnHover(
        context: InteractionVisualStateContext,
        hoveredObject: THREE.Object3D,
    ): void {
        void context;
        void hoveredObject;
    }
}
//------ 特征基础视觉状态 结束------
